<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Cyclone Couriers Settings" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>

        <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1">
                <div class="sm:pl-0 sm:pr-5">
                    <h3 class="text-base font-semibold leading-6 text-gray-800">
                        Details
                    </h3>
                    <p class="mt-2 text-sm text-gray-500">
                        Connect to Cyclone Couriers for delivery services.
                        <br /><br />If you don't have a Cyclone account yet,
                        you can learn how to
                        <a
                            class="text-indigo-600"
                            href="https://www.cyclone.ie"
                            target="_blank"
                        >
                            create one here
                        </a>
                    </p>
                </div>
            </div>
            <div
                v-if="!isLoading"
                class="mt-5 w-full bg-white p-2 text-left shadow sm:rounded-md sm:p-4 md:col-span-2 md:mt-0"
            >
                <div
                    v-if="isConnected"
                    class="mb-4 rounded-md bg-green-50 p-4"
                >
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg
                                class="h-5 w-5 text-green-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm font-medium text-green-800">
                                Connected to Cyclone Couriers
                            </p>
                        </div>
                    </div>
                </div>

                <base-input-field
                    id="uuid"
                    label="API Key (UUID)"
                    v-model="settings.uuid"
                    class="mb-4"
                    :errors="$v.settings.uuid.$error"
                    placeholder="Enter your Cyclone API key"
                />

                <base-input-field
                    id="account-code"
                    label="Account Code"
                    v-model="settings.accountCode"
                    class="mb-4"
                    :errors="$v.settings.accountCode.$error"
                    placeholder="Enter your Cyclone account code"
                />

                <div class="mb-4">
                    <label
                        class="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Collection Address ID
                    </label>
                    <p class="mt-1 text-xs text-gray-500">
                        Search for your collection address using Cyclone's address lookup.
                    </p>
                    <div
                        v-if="settings.collectionPlaceId && !isEditingAddress"
                        class="mt-2 flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 p-3"
                    >
                        <div class="flex-1">
                            <p class="text-sm font-medium text-gray-700">
                                {{ selectedAddressDisplay || settings.collectionPlaceId }}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            @click="startEditingAddress"
                            :disabled="!settings.uuid"
                        >
                            Change
                        </button>
                    </div>
                    <div
                        v-else
                        v-click-outside="onClickOutside"
                        class="relative mt-2 overflow-visible"
                    >
                        <div
                            class="pointer-events-none absolute inset-y-0 left-0 z-0 flex items-center pl-3"
                        >
                            <base-search-icon class="h-4 w-4 text-gray-600" />
                        </div>
                        <input
                            type="text"
                            name="address-search"
                            id="address-search"
                            class="block w-full rounded-md border-gray-300 pl-9 pr-12 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Start typing to search for your address"
                            @input="onAddressInput"
                            v-model="addressSearchQuery"
                            autocomplete="off"
                            :disabled="!settings.uuid"
                        />
                        <div
                            v-if="isSearchingAddress"
                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4"
                        >
                            <span class="spinner text-gray-500 sm:text-sm"></span>
                        </div>
                        <ul
                            v-if="showAddressSuggestions && addressSuggestions.length"
                            class="focus:outline-none absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm"
                            tabindex="-1"
                            role="listbox"
                        >
                            <li
                                v-for="suggestion in addressSuggestions"
                                :key="suggestion.placeId"
                                @click="selectAddress(suggestion)"
                                class="relative my-1 cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-gray-50"
                                role="option"
                            >
                                <div class="flex flex-col">
                                    <span class="mb-0 block truncate text-sm font-medium text-gray-700">
                                        {{ suggestion.address }}
                                    </span>
                                </div>
                            </li>
                        </ul>
                        <button
                            v-if="isEditingAddress && settings.collectionPlaceId"
                            type="button"
                            class="mt-2 text-sm text-gray-500 hover:text-gray-700"
                            @click="cancelEditingAddress"
                        >
                            Cancel
                        </button>
                    </div>
                    <div
                        v-if="!settings.uuid"
                        class="mt-2 text-xs text-gray-500"
                    >
                        Enter your API key first to search for addresses.
                    </div>
                </div>

                <div class="mb-4">
                    <label
                        for="vehicle"
                        class="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Default Vehicle Type
                    </label>
                    <p class="mt-1 text-xs text-gray-500">
                        This vehicle type will be used when no service size brackets match.
                    </p>
                    <select
                        id="vehicle"
                        name="vehicle"
                        v-model="settings.vehicle"
                        class="form-input mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    >
                        <option
                            v-for="vehicleOption in vehicleOptions"
                            :key="vehicleOption.value"
                            :value="vehicleOption.value"
                        >
                            {{ vehicleOption.label }}
                        </option>
                    </select>
                </div>

                <div class="mb-4">
                    <label
                        for="service"
                        class="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Service Type
                    </label>
                    <select
                        id="service"
                        name="service"
                        v-model="settings.service"
                        class="form-input mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    >
                        <option
                            v-for="serviceOption in serviceOptions"
                            :key="serviceOption.value"
                            :value="serviceOption.value"
                        >
                            {{ serviceOption.label }}
                        </option>
                    </select>
                </div>

                <div class="mb-4">
                    <label
                        for="enabled"
                        class="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Integration Status
                    </label>
                    <div class="mt-2 flex items-start">
                        <toggle-switch
                            v-model="settings.enabled"
                            :is-disabled="!isConnected"
                        />
                        <p class="pl-4 text-sm text-gray-500">
                            {{
                                settings.enabled
                                    ? 'Enabled - sending orders to Cyclone Couriers'
                                    : 'Disabled - not sending orders to Cyclone Couriers'
                            }}
                        </p>
                    </div>
                    <div
                        v-if="!isConnected"
                        class="mt-2 text-xs text-red-600"
                    >
                        Please save valid credentials first before enabling the integration.
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-8 md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1">
                <div class="sm:pl-0 sm:pr-5">
                    <h3 class="text-base font-semibold leading-6 text-gray-800">
                        Service Size Brackets
                    </h3>
                    <p class="mt-2 text-sm text-gray-500">
                        Define custom vehicle size brackets based on order subtotal. Orders will be assigned the appropriate vehicle size based on these rules.
                    </p>
                    <p class="mt-2 text-xs text-gray-400">
                        Available sizes: Push Bike, Bike, Top Box Bike, Cargo Bike, Cargo Bike Max, Small Van, Large Van
                    </p>
                </div>
            </div>
            <div class="mt-5 md:col-span-2 md:mt-0">
                <div class="shadow sm:overflow-hidden sm:rounded-md">
                    <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                        <rule-builder
                            v-model="settings.serviceSizeBrackets"
                            :fields="serviceSizeBracketFieldsWithCurrency"
                            label="Service Size Rules"
                            description="Define the order subtotal ranges for each vehicle size."
                            addButtonText="Add Size Bracket"
                            emptyMessage="No size brackets defined. Default vehicle type will be used."
                            :defaultRule="defaultServiceSizeBracket"
                            :validate="validateServiceSizeBracket"
                        />
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
            <span class="inline-flex rounded-md shadow-sm" @click="saveSettings">
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import vClickOutside from 'v-click-outside';

import ToggleSwitch from '@/components/formComponents/ToggleSwitch.vue';
import RuleBuilder from '@/components/formComponents/RuleBuilder.vue';
import BaseSearchIcon from '@/components/base/icons/BaseSearchIcon';
import { debounce } from '@/helpers/debounce';

export default {
    name: 'CycloneSettings',
    metaInfo: {
        title: 'Cyclone Couriers Integration'
    },
    components: { ToggleSwitch, RuleBuilder, BaseSearchIcon },
    directives: {
        clickOutside: vClickOutside.directive
    },
    props: {
        venueId: {
            type: Number,
            required: true
        },
        venue: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            isConnected: false,
            isSearchingAddress: false,
            isEditingAddress: false,
            showAddressSuggestions: false,
            addressSearchQuery: '',
            addressSuggestions: [],
            selectedAddressDisplay: '',
            settings: {
                uuid: '',
                accountCode: '',
                collectionPlaceId: '',
                vehicle: 'BIKE',
                service: 'STANDARD',
                enabled: false,
                serviceSizeBrackets: []
            },
            vehicleOptions: [
                { value: 'PUSHBIKE', label: 'Push Bike' },
                { value: 'BIKE', label: 'Bike' },
                { value: 'TOP-BOX-BIKE', label: 'Top Box Bike' },
                { value: 'CARGO-BIKE', label: 'Cargo Bike' },
                { value: 'CARGO-BIKE-MAX', label: 'Cargo Bike Max' },
                { value: 'SMALL-VAN', label: 'Small Van' },
                { value: 'LARGE-VAN', label: 'Large Van' }
            ],
            serviceOptions: [
                { value: 'STANDARD', label: 'Standard' },
                { value: 'PRIORITY', label: 'Priority' }
            ],
            serviceSizeBracketFields: [
                {
                    key: 'minAmount',
                    label: 'Min Amount',
                    type: 'currency',
                    placeholder: '0.00',
                    required: true
                },
                {
                    key: 'maxAmount',
                    label: 'Max Amount',
                    type: 'currency',
                    placeholder: 'Leave empty for no limit',
                    required: false
                },
                {
                    key: 'vehicle',
                    label: 'Vehicle Size',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'PUSHBIKE', label: 'Push Bike' },
                        { value: 'BIKE', label: 'Bike' },
                        { value: 'TOP-BOX-BIKE', label: 'Top Box Bike' },
                        { value: 'CARGO-BIKE', label: 'Cargo Bike' },
                        { value: 'CARGO-BIKE-MAX', label: 'Cargo Bike Max' },
                        { value: 'SMALL-VAN', label: 'Small Van' },
                        { value: 'LARGE-VAN', label: 'Large Van' }
                    ]
                }
            ],
            defaultServiceSizeBracket: {
                minAmount: 0,
                maxAmount: null,
                vehicle: 'BIKE'
            }
        };
    },
    validations: {
        settings: {
            uuid: { required },
            accountCode: { required }
        }
    },
    computed: {
        currencySymbol() {
            const currencyMap = {
                'GBP': '£',
                'USD': '$',
                'EUR': '€'
            };
            return currencyMap[this.venue?.currencyCode] || '€';
        },
        serviceSizeBracketFieldsWithCurrency() {
            return this.serviceSizeBracketFields.map(field => {
                if (field.type === 'currency') {
                    return {
                        ...field,
                        currencySymbol: this.currencySymbol
                    };
                }
                return field;
            });
        }
    },
    async mounted() {
        await this.fetchSettings();
    },
    methods: {
        onClickOutside() {
            this.showAddressSuggestions = false;
        },
        onAddressInput() {
            if (this.addressSearchQuery.length < 3 || !this.settings.uuid) {
                return;
            }

            this.isSearchingAddress = true;
            this.debounceAddressSearch();
        },
        debounceAddressSearch: debounce(function() {
            this.searchAddress();
        }, 500),
        async searchAddress() {
            try {
                this.showAddressSuggestions = true;
                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/cyclone/address-search`,
                    {
                        params: {
                            query: this.addressSearchQuery
                        }
                    }
                );
                this.addressSuggestions = data || [];
            } catch {
                this.$notify({
                    group: 'settings',
                    title: 'Failed to search addresses',
                    type: 'error'
                });
            } finally {
                this.isSearchingAddress = false;
            }
        },
        async selectAddress(suggestion) {
            this.settings.collectionPlaceId = suggestion.placeId;
            this.selectedAddressDisplay = suggestion.description;
            this.addressSearchQuery = suggestion.description;
            this.showAddressSuggestions = false;
            this.isEditingAddress = false;
        },
        startEditingAddress() {
            this.isEditingAddress = true;
            this.addressSearchQuery = '';
            this.addressSuggestions = [];
            this.showAddressSuggestions = false;
        },
        cancelEditingAddress() {
            this.isEditingAddress = false;
            this.addressSearchQuery = '';
            this.addressSuggestions = [];
            this.showAddressSuggestions = false;
        },
        validateServiceSizeBracket(rule) {
            const errors = {};

            if (rule.minAmount === null || rule.minAmount === undefined || rule.minAmount < 0) {
                errors.minAmount = 'Min amount is required and must be >= 0';
            }

            if (rule.maxAmount !== null && rule.maxAmount !== undefined) {
                if (rule.maxAmount < 0) {
                    errors.maxAmount = 'Max amount must be >= 0';
                } else if (rule.maxAmount <= rule.minAmount) {
                    errors.maxAmount = 'Max amount must be greater than min amount';
                }
            }

            if (!rule.vehicle) {
                errors.vehicle = 'Vehicle size is required';
            }

            return errors;
        },
        async fetchSettings() {
            this.isLoading = true;

            try {
                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/settings/cyclone`
                );

                if (data && data.uuid) {
                    this.settings = {
                        uuid: data.uuid || '',
                        accountCode: data.accountCode || '',
                        collectionPlaceId: data.collectionPlaceId || '',
                        vehicle: data.vehicle || 'BIKE',
                        service: data.service || 'STANDARD',
                        enabled: data.enabled || false,
                        serviceSizeBrackets: data.serviceSizeBrackets || []
                    };
                    this.isConnected = true;

                    if (data.collectionPlaceId) {
                        this.selectedAddressDisplay = data.collectionPlaceId;
                    }
                }
            } catch {
                this.isConnected = false;
            } finally {
                this.isLoading = false;
            }
        },
        async saveSettings() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.isLoading = true;

            try {
                await this.$axios.put(
                    `/venues/${this.venueId}/settings/cyclone`,
                    {
                        uuid: this.settings.uuid,
                        accountCode: this.settings.accountCode,
                        collectionPlaceId: this.settings.collectionPlaceId,
                        vehicle: this.settings.vehicle,
                        service: this.settings.service,
                        enabled: this.settings.enabled,
                        serviceSizeBrackets: this.settings.serviceSizeBrackets || []
                    }
                );

                this.isConnected = true;

                this.$notify({
                    group: 'settings',
                    title: 'Settings saved'
                });
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to save settings';

                if (errorMessage.includes('key_invalid')) {
                    this.$notify({
                        group: 'settings',
                        title: 'Invalid API key. Please check your credentials.',
                        type: 'error'
                    });
                } else {
                    this.$notify({
                        group: 'settings',
                        title: errorMessage,
                        type: 'error'
                    });
                }

                this.isConnected = false;
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
